import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../../components/admin/DataTable';
import Modal from '../../components/common/Modal';
import ErrorState from '../../components/common/ErrorState';
import { fetchOrders } from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import {
  ORDER_STATUS,
  getOrderStatusLabel,
  getOrderStatusBadgeClass,
  getPaymentMethodLabel,
  getPaymentStatusLabel,
} from '../../utils/constants';

const OrderManagePage = () => {
  const { showToast } = useToast();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [detailOrder, setDetailOrder] = useState(null);

  const loadOrders = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchOrders();
      setOrders(data);
    } catch (e) {
      console.error('주문 데이터 로드 실패:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusChange = (orderId, newStatus) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === orderId ? { ...o, orderStatus: newStatus } : o))
    );
    showToast('주문 상태가 변경되었습니다.', 'success');
  };

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const now = new Date();
    return orders.filter((o) => {
      const matchStatus = filterStatus === 'all' || o.orderStatus === filterStatus;
      const matchSearch =
        !term ||
        o.id?.toLowerCase().includes(term) ||
        o.senderName?.toLowerCase().includes(term) ||
        o.deliveryPhone?.includes(searchTerm);
      let matchPeriod = true;
      if (periodFilter !== 'all' && o.orderedAt) {
        const days = periodFilter === 'week' ? 7 : 30;
        const diff = (now - new Date(o.orderedAt)) / (1000 * 60 * 60 * 24);
        matchPeriod = diff >= 0 && diff < days;
      }
      return matchStatus && matchSearch && matchPeriod;
    });
  }, [orders, filterStatus, searchTerm, periodFilter]);

  // 매출 요약(필터 결과 기준, 취소 제외)
  const revenueSummary = useMemo(
    () =>
      filteredOrders
        .filter((o) => o.orderStatus !== 'cancelled')
        .reduce((sum, o) => sum + (o.totalAmount || 0), 0),
    [filteredOrders]
  );

  const columns = [
    { key: 'id', label: '주문번호', sortable: true },
    { key: 'senderName', label: '주문자', sortable: true },
    { key: 'deliveryPhone', label: '연락처', sortable: false },
    { key: 'totalAmount', label: '금액', sortable: true, sortType: 'number', render: (p) => formatCurrency(p) },
    {
      key: 'orderStatus', label: '상태', sortable: true,
      render: (status) => (
        <span className={`rounded-full px-2 py-1 text-xs font-medium ${getOrderStatusBadgeClass(status)}`}>
          {getOrderStatusLabel(status)}
        </span>
      ),
    },
    { key: 'orderedAt', label: '주문일', sortable: true, sortType: 'date',
      render: (v) => (v ? new Date(v).toLocaleDateString('ko-KR') : '-') },
    {
      key: 'actions', label: '상태 변경', sortable: false,
      render: (_, row) => (
        <select
          value={row.orderStatus}
          onChange={(e) => { e.stopPropagation(); handleStatusChange(row.id, e.target.value); }}
          onClick={(e) => e.stopPropagation()}
          className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-primary-500"
        >
          {Object.keys(ORDER_STATUS).map((s) => (
            <option key={s} value={s}>{ORDER_STATUS[s].label}</option>
          ))}
        </select>
      ),
    },
  ];

  const statusFilters = [
    { key: 'all', label: '전체' },
    ...Object.keys(ORDER_STATUS).map((s) => ({ key: s, label: ORDER_STATUS[s].label })),
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">주문 관리</h1>
      </div>

      {error ? (
        <ErrorState onRetry={loadOrders} />
      ) : (
        <>
          {/* 검색 + 필터 */}
          <div className="mb-4 space-y-3">
            <input
              type="text"
              placeholder="주문번호 / 주문자 / 연락처로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500 md:w-96"
            />
            <div className="flex flex-wrap items-center gap-2">
              {statusFilters.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilterStatus(f.key)}
                  className={`rounded-md px-4 py-2 ${
                    filterStatus === f.key ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                aria-label="주문 기간 필터"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">전체 기간</option>
                <option value="week">최근 7일</option>
                <option value="month">최근 30일</option>
              </select>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredOrders}
            onRowClick={(o) => setDetailOrder(o)}
            emptyMessage="조건에 맞는 주문이 없습니다."
            loading={loading}
            pageSize={10}
          />

          {!loading && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
              <span>총 {filteredOrders.length}개의 주문이 있습니다.</span>
              <span className="font-medium text-gray-900">
                매출 합계(취소 제외): {formatCurrency(revenueSummary)}
              </span>
            </div>
          )}
        </>
      )}

      {/* 주문 상세 모달 */}
      <Modal
        isOpen={detailOrder !== null}
        onClose={() => setDetailOrder(null)}
        title="주문 상세"
        size="lg"
      >
        {detailOrder && (
          <div className="space-y-5 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-mono font-medium text-gray-900">{detailOrder.id}</span>
              <span className={`rounded-full px-2 py-1 text-xs font-medium ${getOrderStatusBadgeClass(detailOrder.orderStatus)}`}>
                {getOrderStatusLabel(detailOrder.orderStatus)}
              </span>
            </div>

            {/* 주문 항목 */}
            <div>
              <h4 className="mb-2 font-semibold text-gray-700">주문 항목</h4>
              <div className="divide-y rounded-md border border-gray-200">
                {(detailOrder.items || []).map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2">
                    <span className="text-gray-800">
                      {item.wreathName} <span className="text-gray-400">× {item.quantity}</span>
                    </span>
                    <span className="text-gray-900">{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between px-3 py-2 font-semibold">
                  <span>합계</span>
                  <span>{formatCurrency(detailOrder.totalAmount)}</span>
                </div>
              </div>
            </div>

            {/* 배송지 */}
            <div>
              <h4 className="mb-2 font-semibold text-gray-700">배송 정보</h4>
              <dl className="space-y-1 rounded-md bg-gray-50 p-3">
                <div className="flex gap-2"><dt className="w-20 shrink-0 text-gray-500">받는 분</dt><dd className="text-gray-900">{detailOrder.senderName}</dd></div>
                <div className="flex gap-2"><dt className="w-20 shrink-0 text-gray-500">연락처</dt><dd className="text-gray-900">{detailOrder.deliveryPhone}</dd></div>
                <div className="flex gap-2"><dt className="w-20 shrink-0 text-gray-500">주소</dt><dd className="text-gray-900">{detailOrder.deliveryAddress}</dd></div>
              </dl>
            </div>

            {/* 메시지 카드 */}
            {detailOrder.messageCard && (
              <div>
                <h4 className="mb-2 font-semibold text-gray-700">근조 메시지 카드</h4>
                <p className="whitespace-pre-wrap rounded-md border border-dashed border-gray-300 bg-ivory p-3 text-gray-800">
                  {detailOrder.messageCard}
                </p>
              </div>
            )}

            {/* 결제 */}
            <div>
              <h4 className="mb-2 font-semibold text-gray-700">결제 정보</h4>
              <dl className="space-y-1 rounded-md bg-gray-50 p-3">
                <div className="flex gap-2"><dt className="w-20 shrink-0 text-gray-500">결제수단</dt><dd className="text-gray-900">{getPaymentMethodLabel(detailOrder.paymentMethod)}</dd></div>
                <div className="flex gap-2"><dt className="w-20 shrink-0 text-gray-500">결제상태</dt><dd className="text-gray-900">{getPaymentStatusLabel(detailOrder.paymentStatus)}</dd></div>
                <div className="flex gap-2"><dt className="w-20 shrink-0 text-gray-500">주문일시</dt><dd className="text-gray-900">{detailOrder.orderedAt ? formatDateTime(detailOrder.orderedAt) : '-'}</dd></div>
                {detailOrder.deliveredAt && (
                  <div className="flex gap-2"><dt className="w-20 shrink-0 text-gray-500">배송완료</dt><dd className="text-gray-900">{formatDateTime(detailOrder.deliveredAt)}</dd></div>
                )}
              </dl>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OrderManagePage;
